import '../styles/pilot.css';
import { useEffect,useRef,useState } from 'react';
import { useParams } from 'react-router';
import { useNavigate } from 'react-router';
import { convertPilotAssignmentApiDtoToIdb, setPilotAssignmentSuggestions } from '../pilot-assignment/helpers/PilotAssignmentHelpers';
import PilotAssignment from '../pilot-assignment/PilotAssignment';
import PilotAssignmentMap from '../pilot-assignment/PilotAssignmentMap';
import PilotageDetailsDefault from '../pilotage-details/PilotageDetailsDefault';
import PilotageDetailsShip from '../components/PilotageDetailsShip';
import PilotageDetailsObservations from '../components/PilotageDetailsObservations';
import Overlay from '../../../components/layout/overlay/Overlay';
import { PilotAssignmentUpdatedDialog } from '../pilot-assignment/components/dialogs/PilotAssignmentUpdatedDialog';
import { PilotagDetailsMenuStates, PilotageDetailsBottomMenu } from '../components/PilotageDetailsBottomMenu';
import { isNumeric, isObjectNull } from '../../../components/helpers/ObjectHelpers';
import { processPilotAssignment } from '../pilot-assignment/helpers/PilotAssignmentProcessHelpers';
import PilotAssignmentIdbRepository from '../../../repositories/idb/PilotAssignmentIdbRepository';
import PilotApiRepository from '../../../repositories/api/PilotApiRepository';
import { AppContainerContent } from '../../../components/layout/app/AppContainerContent';
import { createPilotageStore } from '../../../components/hooks/usePilotageStore';
import { PilotageStoreContext } from '../../../components/context/PilotageStoreContext';
import { useStoreSelector } from '../../../components/hooks/useStoreSelector';
import { useSetHeaderContent, useWarningNotification } from "components/hooks/useAppStore";

const initialState = {
    hasErrors: false,
    isBusy: true,
    pilotAssignment: null,
    showUpdatedDialog: false
};

function Details() {
    const navigate = useNavigate();
    const {
        id,
        view = PilotagDetailsMenuStates.Information
    } = useParams();

    const updateHeaderContent = useSetHeaderContent();
    const addWarningNotification = useWarningNotification();

    const menuRef = useRef();

    const [{
        hasErrors,
        isBusy,
        pilotAssignment,
        showUpdatedDialog
    }, setState] = useState(initialState);

    const setStateValue = (property, e) => {
        setState((prev) => ({
            ...prev,
            [property]: e
        }));
    }

    const onInitialized = (e) => {
        setState((prev) => ({
            ...prev,
            pilotAssignment: e,
            hasErrors: false,
            isBusy: false
        }));
    }

    const setShowUpdatedDialog = (e) => {
        setStateValue("showUpdatedDialog", e);
    }

    const storeRef = useRef(createPilotageStore());
    const setHasObservation = useStoreSelector(storeRef.current, (state) => state.setHasObservation);
    const setHasChanges = useStoreSelector(storeRef.current, (state) => state.setHasChanges);

    useEffect(() => {
        return (() => {
            storeRef.current = null;
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {

        const initOnIdAsync = async () => {
            await initializeAsync();
            await setSuggestionsAsync(view);
        }
        initOnIdAsync();
        
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    return (
        (!isObjectNull(pilotAssignment)) &&
        <>
            <PilotageStoreContext.Provider value={storeRef.current}>
                <AppContainerContent
                    bottomMenu={
                        <PilotageDetailsBottomMenu
                            ref={menuRef}
                            view={view}
                            pilotAssignment={pilotAssignment}
                        />
                    }>
                    {
                        (!isObjectNull(pilotAssignment)) &&
                            (() => {
                            switch (view) {
                                case PilotagDetailsMenuStates.PilotAssignment:
                                    return (<PilotAssignment
                                        pilotageId={id}
                                        onChange={onPilotAssignmentChange}
                                    />);
                                case PilotagDetailsMenuStates.Map:
                                    return (
                                        navigator.onLine &&
                                        <PilotAssignmentMap
                                            pilotageId={pilotAssignment.pilotage.pilotageId}
                                            shipId={pilotAssignment.pilotage.pilotageShip.shipId}
                                        />
                                    )
                                case PilotagDetailsMenuStates.Ship:
                                    return (
                                        <PilotageDetailsShip
                                            pilotage={pilotAssignment.pilotage}
                                        />
                                    )

                                case PilotagDetailsMenuStates.Observations:
                                    return (
                                        <PilotageDetailsObservations
                                            pilotage={pilotAssignment.pilotage}
                                        />
                                    )

                                default:
                                    return (
                                        <PilotageDetailsDefault
                                            onReload={onUpdatePilotAssignmentAsync}
                                            state={pilotAssignment.pilotage}
                                            isReadOnly={false}
                                        />
                                    );
                            }
                        })()
                    }
                </AppContainerContent>
            </PilotageStoreContext.Provider>
            <Overlay isBusy={isBusy} onReloadClick={onReloadClick} isActive={hasErrors} />

            {
                showUpdatedDialog &&
                <PilotAssignmentUpdatedDialog
                    onClose={onClosePilotAssignmentUpdatedDialogClickAsync}
                    onClick={onClosePilotAssignmentUpdatedDialogClickAsync} />
            }
        </>
    );


    async function initializeAsync() {

        if (!isNumeric(id)) {
            addWarningNotification("Kunne ikke laste inn losoppdraget da oppgitt id ikke er gyldig.");
            navigate('/pilot/pilotages/assigned');
        } else {

            const pilotageId = Number(id);

            if (navigator.onLine) {
                await initializeOnlineAsync(pilotageId);
            } else {
                await initializeOfflineAsync(pilotageId);
            }
        }
    }

    async function initializeOnlineAsync(pilotageId) {

        const pilotAssignment = await fetchPilotageAsync(pilotageId);
        if (!isObjectNull(pilotAssignment)) {
            const idb = await PilotAssignmentIdbRepository.getByPilotageIdAsync(pilotageId);
            let data = pilotAssignment;

            if (!isObjectNull(idb)) {
                const processedPilotAssignment = processPilotAssignment(idb, pilotAssignment);

                await PilotAssignmentIdbRepository.setAsync(processedPilotAssignment);

                data = processedPilotAssignment;

                if (processedPilotAssignment.wasServerUpdated) {
                    setShowUpdatedDialog(false);
                }

            } else {
                await PilotAssignmentIdbRepository.setAsync(pilotAssignment);
            }

            setHasObservation(data.pilotage.hasObservation);

            if (data.hasOwnProperty('commands')) {
                setHasChanges(data.commands.length > 0);
            }

            onInitialized(data);
            initializeHeader(data);

        } else {
            await initializeOfflineAsync(pilotageId);
        }

    }

    async function initializeOfflineAsync(pilotageId) {
        const data = await PilotAssignmentIdbRepository.getByPilotageIdAsync(pilotageId);
        if (!isObjectNull(data)) {
            onInitialized(data);
            initializeHeader(data);
        } else {
            addWarningNotification("Kunne ikke laste inn losoppdraget.");
            navigate('/pilot/pilotages/assigned');
        }
    }

    async function initializeHeader(pilotAssignment) {
        updateHeaderContent(
            pilotAssignment.pilotage.pilotageShip.shipName,
            `${pilotAssignment.pilotage.pilotageNo}`,
            true, false, true);
    }

    async function fetchPilotageAsync(pilotageId) {
        return await PilotApiRepository.getPilotageByIdAsync(pilotageId)
            .then((response) => response.json())
            .then((result) => {
                updateHeaderContent(result.pilotage.pilotageShip.shipName, result.pilotage.pilotageNo.toString(), true, false, true);
                const converted = convertPilotAssignmentApiDtoToIdb(result);
                return converted;
            })
            .catch((error) => {
                console.error(error);
                addWarningNotification("Kunne ikke hente inn oppdraget fra serveren.");
            })
    }

    function onReloadClick() {
        initializeAsync();
    }

    async function onClosePilotAssignmentUpdatedDialogClickAsync() {
        const pilotageId = Number(id);
        const data = await PilotAssignmentIdbRepository.getByPilotageIdAsync(pilotageId);

        data.wasServerUpdated = false;

        await PilotAssignmentIdbRepository.setAsync(data);

        setShowUpdatedDialog(false);
    }

    async function onUpdatePilotAssignmentAsync(pilotagePilot) {
        await PilotAssignmentIdbRepository.setAsync(pilotagePilot);
        initializeAsync();
    }

    function onPilotAssignmentChange() {
        menuRef.current.onPilotAssignmentChange();
    }

    async function setSuggestionsAsync(newPageState) {
        if (newPageState !== PilotagDetailsMenuStates.PilotAssignment) return;
        await setPilotAssignmentSuggestions(Number(id));
    }
}

export default Details;
